/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { guid, TimeSpan, TimestampRange, UTC } from 'rewire-common';
import { freeze } from 'rewire-core';
import { Form, IFormContext, IActionMenuItem } from 'rewire-ui';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import UndoIcon from '@material-ui/icons/UndoOutlined';
import RestoreIcon from '@material-ui/icons/RestoreOutlined';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import LogIcon from '@material-ui/icons/AssignmentOutlined';
import SwapIcon from '@material-ui/icons/SwapHorizOutlined';
import {
  IJob,
  IShift,
  ITemplate,
  ITimecardTemplate,
  ITimecard,
  IOccupation,
  IEmployee,
  IMessage,
  TimecardInput,
  SaveTimecardInput,
  IUdf,
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import DataMutations from '../graphql/Mutations';
import { notifications } from '../ui/Notifications';
import setViewOnlyForFormFields from '../utility/setViewOnlyForFormFields';
import { getNameSearcher } from '../utility/Searchers';
import { timecardTemplateToTimecard, timecardToTimecardInput } from '../utility/DataConverters';
import infoPanelShiftViewModel, { IInfoPanelShiftViewModelChangeHandler, IShiftInfo } from './InfoPanelShiftViewModel';
import InfoPanelViewModelBase from './InfoPanelViewModelBase';
import scheduleModel from './ScheduleModel';
import { EditShiftModel } from '../timecard/edit/EditTimecardModel';
import groupBy from 'lodash/groupBy';

import authService from '../services/AuthService';
import { toHours, toTimestamp } from '../timecard/edit/TimecardEntries';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';

export class InfoPanelShiftDetailsViewModel extends InfoPanelViewModelBase implements IInfoPanelShiftViewModelChangeHandler {
  editShiftModel: EditShiftModel;
  _optionsActionMenuItems: IActionMenuItem[];

  constructor() {
    super();
    this.editShiftModel = new EditShiftModel('shiftEditor', () => scheduleModel.viewOnly);
    this.editShiftModel.addListener('edit', this.onEdit);
    this.editShiftModel.addListener('save', this.onSave);

    this._optionsActionMenuItems = [
      {
        name: 'duplicateShift',
        title: 'Duplicate Shift(s)',
        icon: FileCopyIcon,
        onClick: () => this.showDuplicateShiftDialog(),
        closeOnClick: true,
        disabled: () => this.viewOnly || !this.editing || this.isNew || this.hasChanges(),
        visible: () => scheduleModel.viewMode === 'Job',
      },
      {
        name: 'deleteShift',
        title: 'Delete Shift(s)',
        icon: DeleteIcon,
        onClick: () => this.deleteShift(),
        closeOnClick: true,
        disabled: () => this.viewOnly || !this.editing || this.isNew,
      },
      {
        name: 'resetToDefault',
        title: 'Reset to Default',
        icon: UndoIcon,
        onClick: () => this.resetShiftToDefault(),
        closeOnClick: true,
        disabled: () => this.viewOnly,
        visible: () => scheduleModel.viewMode === 'Job',
      },
      {
        name: 'undoAbsence',
        title: 'Undo Absence',
        icon: RestoreIcon,
        onClick: () => this.undoAbsence(),
        closeOnClick: true,
        disabled: () => this.viewOnly || !this.shiftHasAbsenceReason || this.isNew,
      },
      {
        name: 'swapEmployees',
        title: 'Swap Employees',
        icon: SwapIcon,
        onClick: () => scheduleModel.swapShiftEmployees(),
        closeOnClick: true,
        disabled: () => !scheduleModel.canSwapShiftEmployees(),
        visible: () => scheduleModel.viewMode === 'Job',
      },
      {
        name: 'addComment',
        title: 'Add Comment',
        icon: AddIcon,
        onClick: () => this.addComment(),
        closeOnClick: true,
        disabled: () => this.viewOnly,
      },
      { name: 'auditLog', title: 'View Audit Log', icon: LogIcon, onClick: () => this.viewAuditLog(), closeOnClick: true },
    ];

    const templateSearcher = getNameSearcher('templates');
    this.form = Form.create(
      (form: IFormContext) => ({
        area: form.static().label('Area'),
        group: form.static().label('Group'),
        occupation: form.static().label('Occupation'),
        employee: form.static().label('Employee'),
        date: form.static().label('Date'),
        template: form.reference(templateSearcher).label('Shift Type').disableErrors().onValueChange(this.onTemplateChange),
      }),
      {},
      {
        defaultAdornmentsEnabled: false,
        variant: 'outlined',
        updateOnChange: true,
        useTooltipForErrors: true,
      },
    );

    setViewOnlyForFormFields(this.form, () => this.viewOnly);

    this.action('undo', this.undo, { disabled: () => !this.canUndo() }).action('save', this.submit, {
      type: 'submit',
      icon: 'save',
      disabled: () => !this.canSave(),
    });
  }

  get optionsActionMenuItems(): IActionMenuItem[] {
    authService.customModule?.getOptionsActionMenuItems?.call(this, scheduleModel, this._optionsActionMenuItems);
    return this._optionsActionMenuItems;
  }

  reset() {
    this.form.reset();
    freeze(() => {
      this.editShiftModel.reset();
    });
  }

  load = async () => {
    this.editShiftModel.viewMode = scheduleModel.viewMode;
    this.state.enable = false;
    this.reset();
    this.editShiftModel.autoCreateVacancies = this.job?.fields?.['_autoCreateVacancyForAbsenceReasons:b'] !== false;

    this.loadShiftEditor();
    this.state.enable = true;
  };

  loadShiftEditor() {
    const shift = this.currentInnerShift;
    if (!shift) {
      this.reset();
      return;
    }

    if (!shift.defaultTimecard) {
      return;
    }

    const template = shift.defaultTimecard?.template || shift.details[0]?.template;
    const area = shift.details[0]?.group?.area?.name || this.job?.group?.area?.name;
    const group = shift.details[0]?.group?.name || this.job?.group?.name;
    const occupation = shift.details[0]?.occupation?.name || this.occupation?.name || 'No Occupation';
    const employee = shift.details[0]?.employee?.name || this.employee?.name || 'No Employee';
    this.form.value = {
      area,
      group,
      occupation,
      employee,
      date: this.toDisplayDateString(this.earnedDate),
      template: template,
    };

    this.form.value = this.form.toObjectValues(); // Acts as a "commit".
    // this.editShiftModel.setVisibility('employee', showEmployee);
    this.editShiftModel.load(shift.defaultTimecard, template, shift.details, this.form.hasChanges);
  }

  onCurrentInnerShiftChange() {
    this.loadShiftEditor();
  }

  protected onEdit = () => {
    const shift = this.currentInnerShift;
    if (!shift) return;
    const timecards = this.editShiftModel.getTimecards();
    if (!timecards) return;
    shift.hasChanges = this.editShiftModel.hasChanges;
    shift.details.length = 0;
    shift.details.set(timecards);
  };

  protected onSave = () => {
    setTimeout(() => this.save(), 100);
  };

  get shift(): IShift | undefined {
    return infoPanelShiftViewModel.shift;
  }

  get isNewInnerShift(): boolean {
    return infoPanelShiftViewModel.isNewInnerShift;
  }

  get innerShifts(): IShiftInfo[] {
    return infoPanelShiftViewModel.innerShifts;
  }

  get currentInnerShift(): IShiftInfo | undefined {
    return infoPanelShiftViewModel.currentInnerShift;
  }

  get isNew(): boolean {
    return infoPanelShiftViewModel.isNew;
  }

  get viewOnly(): boolean {
    return infoPanelShiftViewModel.viewOnly;
  }

  get template(): ITemplate | undefined {
    return this.form.field.template.value;
  }

  get occupation(): IOccupation | undefined {
    return infoPanelShiftViewModel.occupation;
  }

  get employee(): IEmployee | undefined {
    return infoPanelShiftViewModel.employee;
  }

  get earnedDate(): UTC {
    return infoPanelShiftViewModel.earnedDate;
  }

  get job(): IJob | undefined {
    return infoPanelShiftViewModel.job;
  }

  get jobId(): string | undefined {
    return infoPanelShiftViewModel.jobId;
  }

  get jobGroupId(): string | undefined {
    return infoPanelShiftViewModel.jobGroupId;
  }

  get jobAutoCreateVacancyForAbsenceReasons(): boolean {
    return infoPanelShiftViewModel.jobAutoCreateVacancyForAbsenceReasons;
  }

  get editing(): boolean {
    // return !!this.getShifts().next().value;
    return infoPanelShiftViewModel.editing && !!this.shift;
  }

  get templateTimecardTemplates(): ITimecardTemplate[] | undefined {
    return this.template?.template;
  }

  get shiftHasAbsenceReason(): boolean {
    return infoPanelShiftViewModel.shiftHasAbsenceReason;
  }

  get messages(): IMessage[] {
    if (scheduleModel.viewMode === 'Job') {
      return scheduleModel.messagesSubscriptionModel.messages.filter(
        (m) => m.jobId === this.job?.id && this.earnedDate && m.effective?.equals(this.earnedDate),
      );
    }
    return scheduleModel.messagesSubscriptionModel.messages.filter(
      (m) => m.employee?.id === this.employee?.id && this.earnedDate && m.effective?.equals(this.earnedDate),
    );
  }

  hasChanges(): boolean {
    return infoPanelShiftViewModel.hasChanges();
  }

  async getTemplateTimecardTemplates(templateId?: string): Promise<ITimecardTemplate[] | undefined> {
    if (!templateId) {
      return undefined;
    }
    const returnSchema = `
      id
      name
      template {
        start
        end
        units
        factor
        rate
        amount
        accountCode
        reason {
          id
          name
          category
        }
        overtimeReason {
          id
          name
        }
      }
    `;

    const result = await DataQueries.getTemplatesByIds(returnSchema, [templateId]);
    if (result?.[0]?.template) {
      const template = result[0];
      const timecardTemplates = template?.template;
      return timecardTemplates as ITimecardTemplate[];
    }
    return undefined;
  }

  onTemplateChange = async (_form: Form, value: any) => {
    // used by Job Mode Only
    const details: Partial<ITimecard>[] = [];
    if (value) {
      const shift = this.currentInnerShift;
      if (!shift || value?.id === shift.template) return;
      const timecardTemplates = await this.getTemplateTimecardTemplates(value.id);
      if (!this.template) return;
      this.template.template = timecardTemplates ?? [];
      if (timecardTemplates) {
        const employee = shift.details.find((d) => !!d.employee)?.employee ?? shift.defaultTimecard.employee;
        const job = shift.details.find((d) => !!d.job)?.job ?? shift.defaultTimecard.job;
        const group = job?.group ?? this.job?.group ?? shift.defaultTimecard.group;
        const occupation = job?.occupation ?? this.job?.occupation ?? shift.defaultTimecard.occupation;
        const reason = shift.details.find((d) => !!d.reason && d.reason.category === 'worked')?.reason;
        details.push(
          ...timecardTemplates.map((tt: ITimecardTemplate) =>
            timecardTemplateToTimecard({
              timecardTemplate: tt,
              template: value,
              earnedDate: this.earnedDate,
              group,
              job,
              reason,
              occupation,
              employee,
            }),
          ),
        );
      }
      shift.details.set(details as ITimecard[]);
      shift.template = value;
      shift.defaultTimecard.template = value;
      shift.hasChanges = true;
      this.loadShiftEditor();
    }
  };

  showDuplicateShiftDialog() {
    infoPanelShiftViewModel.showDuplicateShiftDialog();
  }

  deleteShift() {
    infoPanelShiftViewModel.deleteShifts();
  }

  canRemoveInnerShift = () => {
    return infoPanelShiftViewModel.canRemoveInnerShift();
  };

  removeInnerShift = () => {
    if (this.innerShifts.length <= 1) {
      this.form.field.template.value = undefined;
      this.editShiftModel.deleteTimecard();
    }
    infoPanelShiftViewModel.removeInnerShift();
  };

  resetShiftToDefault() {
    infoPanelShiftViewModel.resetShiftToDefault();
  }

  undoAbsence() {
    infoPanelShiftViewModel.undoAbsence();
  }

  addComment() {
    infoPanelShiftViewModel.addComment();
  }

  viewAuditLog() {
    infoPanelShiftViewModel.viewAuditLog();
  }

  toDisplayDateString(utc?: UTC): string {
    return infoPanelShiftViewModel.toDisplayDateString(utc);
  }

  canUndo(): boolean {
    return this.canSave();
  }

  undo = async () => {
    await infoPanelShiftViewModel.loadInnerShifts();
    this.load();
    return false;
  };

  canSave(): boolean {
    return !this.viewOnly && (this.hasChanges() || this.editShiftModel.hasChanges);
  }

  private createTimecardInputFromDetail(
    udfs: IUdf[],
    detail: ITimecard,
    selectedDetail: ITimecard,
    shiftNbr: number,
    earnedDate: UTC,
    effective: TimestampRange,
  ): ITimecard {
    const timecard: ITimecard = {
      id: guid(),
      accountCode: detail.accountCode ?? selectedDetail.accountCode,
      rate: selectedDetail.rate,
      factor: selectedDetail.factor,
      tags: selectedDetail.tags,
      group: selectedDetail.group,
      overtimeReason: selectedDetail.overtimeReason,
      employee: detail.employee?.id === 'none' ? selectedDetail.employee : detail.employee,
      occupation: detail.occupation?.id === 'none' ? selectedDetail.occupation : detail.occupation,
      reason: detail.reason?.id === 'none' && selectedDetail.reason?.category === 'worked' ? selectedDetail.reason : detail.reason,
      template: detail.template?.id === 'none' ? selectedDetail.template : detail.template,
      job: selectedDetail.job,
      earnedDate,
      units: effective.toUnits(),
      effective: new TimestampRange(effective),
      shift: shiftNbr,
    };
    if (selectedDetail.fields) {
      timecard.fields = Object.assign({}, selectedDetail.fields);
    }
    timecard.amount = (timecard.units ?? 0) * (timecard.rate ?? 0);
    // let fields = '';
    if (detail.fields) {
      timecard.fields = timecard.fields ?? {};
      for (const udf of udfs) {
        if (udf.type !== 'reference') {
          continue;
        }
        const value = detail.fields?.[udf.name as string] as any;
        if (value?.id !== 'none') {
          (timecard as any).fields[udf.name as string] = value;
        }
        // fields += `${udf.name}: ${(timecard as any).fields[udf.name as string]?.name ?? ''}\n`;
      }
    }

    // console.log('--------------------------------------------------');
    // console.log(
    //   `
    // Employee: ${timecard.employee?.name}
    // Date: ${timecard.earnedDate.toDateString()}
    // Effective: ${timecard.effective!.toTimeString()}
    // Units: ${timecard.units}
    // Occupation: ${timecard.occupation?.name}
    // Reason: ${timecard.reason?.name}
    // Template: ${timecard.template?.name}
    // Shift: ${timecard.shift}
    // Fields: ${fields}
    // `,
    // );

    return timecard;
  }

  private generateDetailWithDefaults(
    timecardInputs: TimecardInput[],
    missingPieces: { earnedDate: UTC; effective: TimestampRange; defaultTimecard: ITimecard; detail: ITimecard }[],
    udfs: IUdf[],
    defaultTimecard: ITimecard | undefined,
    earnedDate: UTC,
    effective: TimestampRange,
    shiftNbr: number,
    detail: ITimecard,
    selectedDetails: ITimecard[] | undefined,
  ): TimecardInput[] {
    if (!effective) {
      return timecardInputs;
    }
    // console.log(
    //   `Generating detail with defaults for shift ${shiftNbr} ${earnedDate.toDateString()} with effective ${effective.toTimeString()} ${detail.employee?.name}`,
    // );

    if (selectedDetails?.length === 0) {
      if (effective.toUnits() === 0) {
        return timecardInputs;
      }
      const timecard = this.createTimecardInputFromDetail(udfs, detail, defaultTimecard ?? detail, shiftNbr, earnedDate, effective);
      timecardInputs.push(timecardToTimecardInput(timecard));
      return timecardInputs;
    }

    const accountedForRanges: TimestampRange[] = [];
    if (detail.reason?.category === 'absence') {
      if (
        timecardInputs.some(
          (t) =>
            detail.employee?.id &&
            t.employeeId === detail.employee?.id &&
            t.reasonId === detail.reason?.id &&
            t.effective?.intersects(effective),
        )
      ) {
        return timecardInputs;
      }

      const intersectingDetail = selectedDetails?.find((d) => d.effective?.intersects(effective));
      if (intersectingDetail) {
        const employeeId = detail.employee?.id === 'none' ? intersectingDetail.employee?.id : detail.employee?.id;
        if (timecardInputs.some((t) => employeeId && t.employeeId === employeeId && t.effective?.intersects(effective))) {
          return timecardInputs;
        }

        const timecard = this.createTimecardInputFromDetail(udfs, detail, intersectingDetail, shiftNbr, earnedDate, effective);
        timecardInputs.push(timecardToTimecardInput(timecard));
      }
      return timecardInputs;
    }

    for (const selectedDetail of selectedDetails ?? []) {
      if (!selectedDetail.effective || selectedDetail.shift !== shiftNbr) {
        continue;
      }

      if (selectedDetail.reason?.category !== 'worked' && selectedDetail.reason?.category !== 'break') {
        continue;
      }
      const intersection = effective.intersection(selectedDetail.effective);
      if (intersection === TimestampRange.Empty) {
        // accountedForRanges.push(effective);
        continue;
      }

      accountedForRanges.push(intersection);

      const employeeId = detail.employee?.id === 'none' ? selectedDetail.employee?.id : detail.employee?.id;
      if (timecardInputs.some((t) => employeeId && t.employeeId === employeeId && t.effective?.intersects(intersection))) {
        continue;
      }

      const timecard = this.createTimecardInputFromDetail(udfs, detail, selectedDetail, shiftNbr, earnedDate, intersection);
      timecardInputs.push(timecardToTimecardInput(timecard));
    }
    if (!defaultTimecard) {
      return timecardInputs;
    }

    const pieces = Array.from(TimestampRange.subtract([effective], accountedForRanges));

    for (const missingPiece of pieces) {
      if (missingPiece.isEmpty) {
        continue;
      }

      missingPieces.push({ earnedDate, effective: missingPiece, defaultTimecard, detail });
    }

    return timecardInputs;
  }

  private generateDetailsForMultiSelectShift(
    timecardInputs: TimecardInput[],
    missingPieces: { earnedDate: UTC; effective: TimestampRange; defaultTimecard: ITimecard; detail: ITimecard }[],
    udfs: IUdf[],
    defaultTimecard: ITimecard | undefined,
    earnedDate: UTC,
    selectedDetails: ITimecard[] | undefined,
  ): void {
    for (const absenceDetail of selectedDetails ?? []) {
      if (absenceDetail.reason?.category === 'absence') {
        timecardInputs.push(timecardToTimecardInput(absenceDetail));
      }
    }

    for (let idx = 0; idx < this.innerShifts.length; idx++) {
      const shiftDetails = this.innerShifts[idx];
      if (!shiftDetails.details?.length) {
        this.generateDetailWithDefaults(
          timecardInputs,
          missingPieces,
          udfs,
          shiftDetails.defaultTimecard,
          earnedDate,
          new TimestampRange(earnedDate, earnedDate),
          idx,
          {} as ITimecard,
          [],
        );
        continue;
      }

      const isMultiShiftSelection = infoPanelShiftViewModel.state.isMultiShiftSelection;
      const detailsForShift = [] as ITimecard[];
      for (const shiftDetail of selectedDetails ?? []) {
        if ((isMultiShiftSelection || shiftDetail.shift === idx) && shiftDetail.reason?.category !== 'absence') {
          detailsForShift.push(shiftDetail);
        }
      }

      defaultTimecard =
        detailsForShift?.find((d) => d.reason?.category === 'worked') ?? defaultTimecard ?? shiftDetails.defaultTimecard;
      if (defaultTimecard.template?.id === 'none') {
        const timecard = selectedDetails?.find((d) => !!d.template && d.template.id !== 'none');
        defaultTimecard = {
          ...defaultTimecard,
        };
        defaultTimecard.effective = timecard?.effective ?? defaultTimecard.effective;
        defaultTimecard.template = timecard?.template ?? defaultTimecard.template;
      }
      let start = defaultTimecard?.effective?.start ?? earnedDate;

      for (const detail of shiftDetails.details) {
        let effective: TimestampRange | undefined;
        if (!detail.effective) {
          if (!detail.units) {
            continue;
          }
          effective = new TimestampRange(start, start.add(detail.units, TimeSpan.hours, 4));
        } else {
          const start = toHours(detail.earnedDate, detail.effective.start);
          const end = toHours(detail.earnedDate, detail.effective.end);
          effective = new TimestampRange(toTimestamp(earnedDate, start), toTimestamp(earnedDate, end));
        }

        if (detailsForShift.length === 0) {
          this.generateDetailWithDefaults(
            timecardInputs,
            missingPieces,
            udfs,
            defaultTimecard ?? shiftDetails.defaultTimecard,
            earnedDate,
            effective,
            idx,
            detail,
            [],
          );
          continue;
        }

        if (isMultiShiftSelection) {
          const groupedDetails = groupBy(detailsForShift, (d) => d.shift);
          for (const shiftNbr in groupedDetails) {
            const detailsForShiftNbr = groupedDetails[shiftNbr];
            const defaultTC = detailsForShiftNbr.find((d) => d.reason?.category === 'worked') ?? defaultTimecard;
            this.generateDetailWithDefaults(
              timecardInputs,
              missingPieces,
              udfs,
              defaultTC,
              earnedDate,
              effective,
              Number(shiftNbr),
              detail,
              detailsForShiftNbr,
            );
          }
        } else {
          this.generateDetailWithDefaults(
            timecardInputs,
            missingPieces,
            udfs,
            defaultTimecard,
            earnedDate,
            effective,
            idx,
            detail,
            detailsForShift,
          );
        }

        if (detail.reason?.category !== 'absence') {
          start = effective.end;
        }
      }
    }
  }

  /**
   * Saves timecards for multiple shifts by processing shift details and applying default values.
   *
   * This method iterates through all shifts and their details, grouping them by shift number.
   * For each detail, it:
   * - Recalculates effective time ranges based on the earned date
   * - Finds matching details by time intersection
   * - Applies default values (employee, occupation, reason, template, job) where details have placeholder IDs ('none')
   * - Converts details to TimecardInput format
   *
   * The timecards are then saved based on the current view mode:
   * - 'Job' mode: saves via DataMutations.saveJobTimecards()
   * - 'Employee' mode: groups timecards by earned date and employee ID before saving
   *
   * @private
   * @async
   * @returns {Promise<boolean>}
   */
  private async saveTimecardsForMultipleShifts(): Promise<boolean> {
    const timecardInputs: TimecardInput[] = [];
    const udfs = udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('timecards');
    const selectedShifts = Array.from(scheduleModel.getShifts());
    // no shifts selected, but user still clicked "Save" from the multi-select shift details view, so we need to process all selected cells as if they are part of one big multi-select shift
    if (selectedShifts.length === 0) {
      for (const shiftInfo of scheduleModel.getSelectedCells()) {
        const [job, earnedDate, _] = shiftInfo;
        let defaultTimecard = this.currentInnerShift?.defaultTimecard;
        if (defaultTimecard) {
          defaultTimecard = Object.assign({}, defaultTimecard);
          const defaultTemplate = defaultTimecard.template?.template;
          if (defaultTemplate) {
            defaultTimecard.reason = defaultTemplate[0]?.reason ?? defaultTimecard.reason;
          }
          defaultTimecard.earnedDate = earnedDate!;
          defaultTimecard.employee = job?.employee;
          defaultTimecard.occupation = job?.occupation;
          defaultTimecard.job = job;
        }
        this.generateDetailsForMultiSelectShift(timecardInputs, [], udfs, defaultTimecard, earnedDate, []);
      }
    } else {
      const missingPieces = [] as { earnedDate: UTC; effective: TimestampRange; defaultTimecard: ITimecard; detail: ITimecard }[];
      for (const selectedShift of selectedShifts) {
        const earnedDate = selectedShift.earnedDate;
        this.generateDetailsForMultiSelectShift(timecardInputs, missingPieces, udfs, undefined, earnedDate, selectedShift.details);
      }

      for (const missingPiece of missingPieces) {
        if (missingPiece.effective.isEmpty) {
          continue;
        }

        const employeeId =
          missingPiece.detail.employee?.id === 'none'
            ? missingPiece.defaultTimecard.employee?.id
            : missingPiece.detail.employee?.id;

        if (
          employeeId === 'none' ||
          timecardInputs.some((t) => employeeId && t.employeeId === employeeId && t.effective?.intersects(missingPiece.effective))
        ) {
          continue;
        }

        const timecard: ITimecard = this.createTimecardInputFromDetail(
          udfs,
          missingPiece.detail,
          missingPiece.defaultTimecard,
          missingPiece.defaultTimecard.shift,
          missingPiece.earnedDate,
          missingPiece.effective,
        );
        timecard.fields = undefined;
        timecardInputs.push(timecardToTimecardInput(timecard));
      }
    }

    if (scheduleModel.viewMode === 'Job' && timecardInputs.length > 0) {
      return !!(await DataMutations.saveJobTimecards(timecardInputs));
    }
    // employee mode
    const timecardEarnedDates = timecardInputs
      .map((t) => t.earnedDate)
      .reduce((prev: UTC[], curr: UTC) => {
        if (!prev.some((p) => p.equals(curr))) {
          prev.push(curr);
        }
        return prev;
      }, []);
    const timecardsToSave = [] as SaveTimecardInput[];
    for (const earnedDate of timecardEarnedDates) {
      timecardsToSave.push({
        earnedDate: earnedDate,
        employeeId: this.employee!.id,
        timecards: timecardInputs.filter((t) => t.earnedDate.equals(earnedDate)),
      });
    }
    return !!(await DataMutations.saveEmployeeTimecards(timecardsToSave));
  }

  private save = async (): Promise<boolean> => {
    if (!this.canSave()) {
      return false;
    }

    let result: boolean;
    if (scheduleModel.isMultiSelect) {
      result = await this.saveTimecardsForMultipleShifts();
    } else {
      const timecardInputs: TimecardInput[] = [];
      let shiftIndex = -1;
      for (let index = 0; index < this.innerShifts.length; index++) {
        const shift = this.innerShifts[index];
        const details = shift.details ?? [];
        if (details.length > 0) ++shiftIndex;
        for (const detail of details) {
          if (scheduleModel.viewMode === 'Job') {
            detail.shift = shiftIndex;
            detail.template = shift.template ?? detail.template;
          }

          const timecard: TimecardInput = timecardToTimecardInput(detail);
          timecardInputs.push(timecard);
        }
      }

      if (!timecardInputs.length) {
        this.deleteShift();
        return true;
      }

      if (scheduleModel.viewMode === 'Job') {
        result = !!(await DataMutations.saveJobTimecards(timecardInputs));
      } else {
        // employee mode
        const timecardEarnedDates = timecardInputs
          .map((t) => t.earnedDate)
          .reduce((prev: UTC[], curr: UTC) => {
            if (!prev.some((p) => p.equals(curr))) {
              prev.push(curr);
            }
            return prev;
          }, []);
        const timecardsToSave = [] as SaveTimecardInput[];
        for (const earnedDate of timecardEarnedDates) {
          timecardsToSave.push({
            earnedDate: earnedDate,
            employeeId: this.employee!.id,
            timecards: timecardInputs.filter((t) => t.earnedDate.equals(earnedDate)),
          });
        }

        result = !!(await DataMutations.saveEmployeeTimecards(timecardsToSave));
      }
    }

    if (result) {
      notifications.success(`Shift ${this.editing ? 'Updated' : 'Created'}`);
      for (const s of this.innerShifts) s.isNew = false;
      infoPanelShiftViewModel.submit();
      return true;
    }

    notifications.error(`Shift ${this.editing ? 'Update' : 'Creation'} Failed`);
    return false;
  };

  submit = async () => {
    await this.form.submitAsync(this.save);
    return false;
  };
}

const infoPanelShiftDetailsViewModel = new InfoPanelShiftDetailsViewModel();
export default infoPanelShiftDetailsViewModel;
